import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";

const CREATORS = [
  {
    name: "Sanket Apte",
    role: "Creator",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_books-young-readers/artifacts/41pn284c_Gemini_Generated_Image_.png",
  },
  {
    name: "Shruti Kulkarni",
    role: "Co-creator",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_books-young-readers/artifacts/0b8lrt9w_Gemini_Generated_Image_6qdz5a6qdz5a6qdz%20%281%29.webp",
  },
];

const CHAPTERS = [
  {
    n: "01",
    title: "Why we started",
    body:
      "Books for Little Hands began at a kitchen table, with a real question from a real child: “What is money?” It grew into a small, careful publishing project designed to answer big questions in small, gentle voices.",
  },
  {
    n: "02",
    title: "How we make our books",
    body:
      "Every story is scripted, illustrated with the help of AI, and then thoughtfully edited by the creators — so warmth, values, and voice stay unmistakably human. Each book pairs the story with hands-on activities to turn ideas into practice.",
  },
  {
    n: "03",
    title: "What we teach",
    body:
      "Two living series today: Finance for Little Hands (money, banks, needs vs wants) and AI for Little Hands (computers, everyday AI helpers, safe & smart choices). Both build lifelong literacies while children are still curious about everything.",
  },
  {
    n: "04",
    title: "Where we go next",
    body:
      "More books, more topics, and more small hands turning pages. If you'd like to hear when the next chapter arrives, join the newsletter on our homepage.",
  },
];

export default function AboutPage() {
  useLenis();
  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--paper)" }}
      data-testid="about-page"
    >
      <div className="grain-overlay" aria-hidden="true" />

      {/* Top bar */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="about-brand">
          <span
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-[8px]"
            style={{ background: "var(--ink)" }}
          >
            <span className="font-serif italic font-bold text-xl leading-none text-[color:var(--paper)]">
              B
            </span>
            <span
              className="absolute rounded-full"
              style={{
                width: "5px",
                height: "5px",
                top: "5px",
                right: "6px",
                background: "var(--accent-orange)",
              }}
            />
          </span>
          <span className="font-serif italic text-xl">Books for Little Hands</span>
        </Link>
        <Link to="/" className="btn-outline !py-2 !px-4 !text-[11px]">
          ← Home
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <p className="folio-num">Colophon · About us</p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="mt-4 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight"
        >
          Big ideas.{" "}
          <em className="italic" style={{ color: "var(--accent-orange)" }}>
            Small voices.
          </em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.9 }}
          className="mt-6 max-w-2xl text-lg leading-relaxed text-stone-700"
        >
          Books for Little Hands is an independent picture-book studio building
          quiet, curious series that teach children the biggest ideas of their
          generation — money, banks, artificial intelligence — in the smallest,
          gentlest voices.
        </motion.p>
      </section>

      {/* Chapters */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.06, duration: 0.8 }}
              className="col-span-12 md:col-span-6 border-t border-stone-900/15 pt-8"
              data-testid={`about-chapter-${c.n}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="font-serif text-5xl leading-none">{c.n}</span>
                <span className="folio-num">§ {c.n}</span>
              </div>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-tight">
                {c.title}
              </h3>
              <p className="mt-4 text-stone-700 leading-relaxed">{c.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Creators */}
      <section
        className="py-20 md:py-28"
        style={{ background: "var(--paper-warm)" }}
        data-testid="about-creators"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <p className="folio-num">The Creators</p>
          <h2 className="mt-3 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
            Two voices behind Maya.
          </h2>
          <div className="mt-12 grid grid-cols-12 gap-8 md:gap-12">
            {CREATORS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.8 }}
                className="col-span-12 md:col-span-6"
              >
                <div className="arch-frame aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.name} — ${p.role}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-5 flex items-baseline justify-between border-b border-stone-900/15 pb-3">
                  <p className="font-serif text-3xl leading-none">{p.name}</p>
                  <span className="folio-num">{p.role}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <p className="folio-num">Read next</p>
        <h2 className="mt-3 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
          Meet Maya on the bookshelf.
        </h2>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/#books" className="btn-buy" data-testid="about-cta-shop">
            Shop the books →
          </Link>
          <Link to="/gift-guide" className="btn-outline" data-testid="about-cta-gift">
            Gift guide
          </Link>
        </div>
      </section>
    </div>
  );
}
